import { useState, useEffect } from "react";

import { withTranslation } from "react-i18next";
import { Box, TextField } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import locationService from "@plannel/services/data/location-service";
import customerService from "@plannel/services/data/customer-service";
import itemService from "@plannel/services/data/item-service";
import prodOrderService from "@plannel/services/data/prod-order-service";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;
const { orderType } = CodeUtil;
const orderTypeOptions = Object.keys(orderType);

const INIT_FORM = {
  orderNum: "",
  orderType: orderTypeOptions[0] ?? null,
  locId: null,
  locCd: "",
  locName: "",
  reqLocId: null,
  reqLocCd: "",
  itemId: null,
  itemCd: "",
  itemName: "",
  orderQty: "",
  uomCd: "",
  dueDate: null,
  orderAmt: "",
  currencyCd: "",
  customerId: null,
  customerCd: "",
  customerName: "",
};

const MpDemandAddModal = (props) => {
  const { t, open, onClose, onConfirm } = props;

  const [form, setForm] = useState(INIT_FORM);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [locationPlant, setLocationPlant] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);

  const retrieveLocations = () => {
    locationService.getLookup({ isLocationMaster: true })
      .then(response => {
          const { results } = response.data;
          const plant = [];

          for (const loc of results) {
            if (loc.locType === "PLANT") {
                plant.push(loc);
            }
          }
          
          setLocationPlant(plant);
      })
      .catch(e => {
          debug(e);
      })
  };

  const retrieveCustomers = () => {
    customerService.getAll()
      .then(response => {
        const { results } = response.data;
        setCustomers(results);
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveItems = () => {
    itemService.getAllItems()
      .then(response => {
        const { result } = response.data;
        const fgItems = result.filter(item => item.itemType === "FG");

        setItems(fgItems);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {

      });
  };

  const checkDuplicateOrderNum = async (orderNum) => {
    if (!orderNum) {
      return;
    }

    const params = {
      orderNum: orderNum,
    };

    const response = await prodOrderService.checkDuplicateOrderNum(params);
    const { isDuplicate } = response.data;
    if (isDuplicate) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_DuplicateOrderNum", { orderNum }) });
      return true;
    }

    return false;
  }
  
  const checkInventory = async (form) => {
    if (!form.itemCd || !form.locCd) {
      return false;
    }

    const params = {
      searchFilters: {
        itemCd: form.itemCd,
        locCd: form.locCd,
      },
    };

    const response = await prodOrderService.checkInventory(params);
    const { isExistInventory } = response.data;
    if (!isExistInventory) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NoLocationItem") });
      return false;
    }

    return true;
  }

  useEffect(() => {
    retrieveLocations();
    retrieveCustomers();
    retrieveItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClose = () => {
    setForm(INIT_FORM);
    onClose();
  };

  const handleNonNegativeChange = (field) => (e) => {
    const { value } = e.target;
    const num = Number(value);

    if (value !== "" && (!Number.isFinite(num) || num < 0)) {
      return;
    }

    setForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleConfirmClick = async () => {
  if (!form.orderNum) {
    setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_OrderNumRequired") });
    return;
  }

  if (form.itemCd === null || form.itemCd === "") {
    setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_NoItemCd") });
    return;
  }

  if (form.locCd === null || form.locCd === "") {
    setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_NoLocCd") });
    return;
  }

  if (form.dueDate === null || form.dueDate === "") {
    setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_NoDate") });
    return;
  }

  const orderQtyNum = Number(form.orderQty);

  if (form.orderQty === null || form.orderQty === "" || !Number.isFinite(orderQtyNum) || orderQtyNum <= 0) {
    setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_NoOrderQty") });
    return;
  }

  const isDuplicate = await checkDuplicateOrderNum(form.orderNum);

  if (isDuplicate) {
    return;
  }

  const isExistInventory = await checkInventory(form);

  if (!isExistInventory) {
    return;
  }

  setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm"), });
};

  const handleDialog = (action) => {
    const payload = {
      ...form,
      orderQty: form.orderQty === "" ? null : Number(form.orderQty),
      orderAmt: form.orderAmt === "" ? null : Number(form.orderAmt),
    };

    if (action === "SAVE") {
      try {
        if (onConfirm) {
          onConfirm(payload);
        }

        handleClose();
      } catch (e) {
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
      }
    }
  };

  return (
    <>
      {/* 아이콘은 PlayArrow — 저장이 아니라 생성이다(DP Process Mgmt 의 "Start DP" 와 맞춤). */}
      <BaseConfigModal
        open={open}
        onClose={handleClose}
        title={t("prodOrderAdd")}
        onSave={handleConfirmClick}
        saveLabel={t("create")}
        saveIcon={<PlayArrowIcon />}
        width={552}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {/* 주문 번호 / 주문 유형 */}
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
              <TextField
                size="small"
                label={t("orderNum")}
                value={form.orderNum}
                sx={{ flex: 1 }}
                onChange={(e) => {
                  setForm(prev => ({
                    ...prev,
                    orderNum: e.target.value,
                  }));
                }}
              />
              <Autocomplete
                size="small"
                options={orderTypeOptions}
                getOptionLabel={(option) => GridUtils.gridValueL10N(orderType[option]) || option}
                value={form.orderType}
                onChange={(_, v) => setForm(p => ({ ...p, orderType: v }))}
                renderInput={(params) => <TextField {...params} label={t("orderType")} />}
                sx={{ flex: 1 }}
              />
            </Box>

            {/* 거점 코드 / 거점 명 */}
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
              <TextField
                size="small"
                label={t("locCd")}
                value={form.locCd}
                InputProps={{ readOnly: true }}
                sx={{ flex: 1, backgroundColor: "#f5f5f5" }}
              />
            <Autocomplete
              size="small"
              options={locationPlant}
              value={locationPlant.find(l => l.locCd === form.locCd) ?? null}
              getOptionLabel={(option) => option.name ?? ""}
              onChange={(_, value) => {
                setForm(prev => ({
                  ...prev,
                  locId: value?.id ?? null,
                  locCd: value?.locCd ?? "",
                  locName: value?.name ?? "",
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("locName")}
                />
              )}
              sx={{ flex: 1 }}
            />
            </Box>

            {/* 품목 코드 / 품목 명 */}
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
              <TextField
                size="small"
                label={t("itemCd")}
                value={form.itemCd}
                InputProps={{ readOnly: true }}
                sx={{ flex: 1, backgroundColor: "#f5f5f5" }}
              />
              <Autocomplete
                size="small"
                options={items}
                value={items.find(l => l.itemCd === form.itemCd) ?? null}
                getOptionLabel={(option) => option.name ?? ""}
                onChange={(_, value) => {
                  setForm(prev => ({
                    ...prev,
                    itemCd: value?.itemCd ?? "",
                    itemName: value?.name ?? "",
                    uomCd: value?.uomCd ?? "",
                    itemId: value?.id ?? null,
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("itemName")}
                  />
                )}
                sx={{ flex: 1 }}
              />
            </Box>

            {/* 단위 / 주문 수량 */}
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
                <TextField
                  size="small"
                  label={t("uomCd")}
                  value={form.uomCd}
                  InputProps={{ readOnly: true }}
                  sx={{ flex: 1, backgroundColor: "#f5f5f5" }}
                />
                <TextField
                  size="small"
                  label={t("orderQty")}
                  onChange={handleNonNegativeChange("orderQty")}
                  value={form.orderQty}
                  sx={{ flex: 1 }}
                  type="number"
                  inputProps={{ min: 0 }}
                />
            </Box>

            {/* 납기일 / 생산 수량 */}
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                    label={t("dueDate")}
                    views={["year", "month", "day"]}
                    format="yyyy-MM-dd"
                    openTo="day"
                    value={form.dueDate}
                    onChange={(v) => setForm(p => ({ ...p, dueDate: v }))}
                    slotProps={{
                    textField: {
                        size: "small",
                        fullWidth: true,
                    },
                    }}
                />
                </LocalizationProvider>
              </Box>
              <TextField
                size="small"
                label={t("orderAmt")}
                onChange={handleNonNegativeChange("orderAmt")}
                value={form.orderAmt}
                sx={{ flex: 1 }}
                type="number"
                inputProps={{ min: 0 }}
              />
            </Box>

            {/* 거래처 코드 / 거래처 명 */}
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
              <TextField
                size="small"
                label={t("customerCd")}
                value={form.customerCd}
                InputProps={{ readOnly: true }}
                sx={{ flex: 1, backgroundColor: "#f5f5f5" }}
              />
              <Autocomplete
                size="small"
                options={customers}
                value={customers.find(c => c.customerCd === form.customerCd) ?? null}
                getOptionLabel={(option) => option.name ?? ""}
                onChange={(_, value) => {
                  setForm(prev => ({
                    ...prev,
                    customerId: value?.id ?? null,
                    customerCd: value?.customerCd ?? "",
                    customerName: value?.name ?? "",
                    currencyCd: value?.currencyCd ?? "",
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("customerName")}
                  />
                )}
                sx={{ flex: 1 }}
              />
            </Box>

            {/* 통화 코드 / (빈칸) */}
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
              <TextField
                size="small"
                label={t("currencyCd")}
                value={form.currencyCd}
                InputProps={{ readOnly: true }}
                sx={{ flex: 1, backgroundColor: "#f5f5f5" }}
              />
              <Box sx={{ flex: 1 }} />
            </Box>
          </Box>
      </BaseConfigModal>

      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
    </>
  );
};

export default withTranslation()(MpDemandAddModal);
